import React, {useState} from 'react';
import {Tab} from "@headlessui/react";
import TabButton from "../../../TabButton";
import Select, {Option} from "../../Select";
import {useFormikContext} from "formik";
import ngage from "../../../../services/ngage";
import {Site, SiteDescriptor} from "../../../../types/Site";
import IconName from "../../../../types/IconName";
import {useOperator} from "../../../../context/OperatorContext";

// Types ----

enum SiteAttachmentMode {ALL = 0, SELECTED = 1}

type SiteAttachmentFieldSetProps = {
  name: string
  selected?: Array<Site|SiteDescriptor>
}

// Helpers ----

const siteOption = (site: SiteDescriptor): Option => ({
  id: site.id,
  name: site.name,
  icon: IconName.OFFICE_BUILDING,
})

const siteSource = async (query: string) => {
  return (await ngage.sites.filtered({search: query || null}))?.data.map(siteOption) || []
}

function initialMode(selected?: Array<Site|SiteDescriptor>): SiteAttachmentMode {
  return selected && selected.length > 0? SiteAttachmentMode.SELECTED : SiteAttachmentMode.ALL;
}

function initiallySelectedSites(selected?: Array<Site|SiteDescriptor>): Array<Option> {
  return selected?.map(siteOption) ?? [];
}

// Component ----

function SiteAttachmentFieldSet({name, selected}: SiteAttachmentFieldSetProps) {
  const {setFieldValue} = useFormikContext();
  const {termFor} = useOperator();

  const [mode, setMode] = useState<SiteAttachmentMode>(initialMode(selected));
  const [selectedSites, setSelectedSites] = useState<Option[]>(initiallySelectedSites(selected));

  function selectMode(mode: SiteAttachmentMode) {
    if (mode === SiteAttachmentMode.ALL) {
      void setFieldValue(name, []);
      setSelectedSites([]);
    }

    setMode(mode);
  }

  function handleSelectSites(sites: Array<Option>) {
    void setFieldValue(name, sites.map(site => site.id));
    setSelectedSites(sites);
  }

  return (
    <Tab.Group selectedIndex={mode} onChange={selectMode}>
      <Tab.List className="mt-1 mb-1 relative z-0 inline-flex shadow-sm rounded-md">
        <TabButton position={TabButton.positions.FIRST}>All</TabButton>
        <TabButton position={TabButton.positions.LAST}>Selected</TabButton>
      </Tab.List>

      <Tab.Panels className='mt-4'>
        <Tab.Panel/>

        <Tab.Panel>
          <Select
            label={termFor('Sites')}
            hint="Type to search"
            source={siteSource}
            selected={selectedSites}
            onChange={handleSelectSites}
            multiple
            required
          />
        </Tab.Panel>
      </Tab.Panels>
    </Tab.Group>
  );
}

// Export ----

export default SiteAttachmentFieldSet;